/**
 * weapons.js — gunfire and weapon mechanics.
 *
 * A convincing synthesised gunshot is three separate sounds that happen to start at
 * the same instant, and the mix between them is the entire character of the weapon:
 *
 *   1. TRANSIENT  (<5 ms)  A broadband click. Carries the "crack" and, because it is
 *                          shorter than the ear's integration time, it reads as
 *                          loudness rather than as pitch. Without it a shot sounds
 *                          like a thump; with it, it sounds dangerous.
 *   2. BODY       (~100 ms) A resonant low tone with a steep downward pitch envelope,
 *                          saturated. This is the expanding gas column, and its
 *                          frequency is the *size* of the weapon: 165 Hz reads as an
 *                          SMG, 88 Hz reads as a marksman rifle. Nothing else in the
 *                          layer stack differentiates calibre as convincingly.
 *   3. TAIL       (300 ms+) Filtered noise, delayed a beat behind the shot, with its
 *                          cutoff falling as it decays. This is the room answering
 *                          back. It is the difference between a shot fired somewhere
 *                          and a shot fired in a vacuum.
 *
 * A mid "crack" band and the action's mechanical tick sit between body and tail.
 *
 * Every parameter that can be jittered is jittered (±3% pitch, ±1 dB level, filter
 * centres, transient brightness) and each weapon is pre-rendered in several
 * variations, because the single most recognisable tell of fake gunfire is hearing
 * the same waveform twice in a row.
 */

import {
  noiseSource, startNoise, noiseBurst, perc, shape, biquad, sweep, waveshaper,
  gain, chain, partial, modal, rand,
} from './dsp.js';

/* --------------------------------------------------------------- gun voices --- */

export const GUN_PRESETS = {
  fire_ar: {
    level: 1.0,
    transient: { level: 2.5, hp: 1500, peak: 3400, decay: 0.0045 },
    body: { freq: 118, decay: 0.105, drive: 2.6, sub: 0.55, punch: 0.5 },
    crack: { freq: 1950, Q: 1.15, decay: 0.05, level: 0.85 },
    tail: { level: 0.5, decay: 0.46, lp: 3100, delay: 0.022 },
    mech: 0.35,
  },
  fire_smg: {
    // Smaller chamber: higher body, much faster decay, brighter crack, tighter tail.
    level: 0.82,
    transient: { level: 2.5, hp: 2100, peak: 4300, decay: 0.0035 },
    body: { freq: 168, decay: 0.055, drive: 2.1, sub: 0.3, punch: 0.42 },
    crack: { freq: 2750, Q: 1.35, decay: 0.032, level: 0.9 },
    tail: { level: 0.38, decay: 0.27, lp: 4000, delay: 0.016 },
    mech: 0.5,
  },
  fire_dmr: {
    // Long barrel, heavy round: deep body, long decay, dark and enormous tail.
    level: 1.0,
    transient: { level: 2.9, hp: 1200, peak: 2900, decay: 0.0055 },
    body: { freq: 86, decay: 0.17, drive: 3.2, sub: 0.8, punch: 0.62 },
    crack: { freq: 1450, Q: 0.95, decay: 0.075, level: 0.8 },
    tail: { level: 0.62, decay: 0.72, lp: 2300, delay: 0.030 },
    mech: 0.3,
  },
  fire_shotgun: {
    // Low, broad and slow. Almost no discrete crack — the transient is wide instead.
    level: 1.0,
    // The transient has to be enormous here precisely because the body is so low: a
    // 66 Hz fundamental with a 33 Hz sub under it takes several milliseconds just to
    // reach its first crest, and without a dominant click the waveform peak lands
    // 30 ms in and the shot reads as a soft whump instead of a discharge.
    transient: { level: 5.2, hp: 800, peak: 2200, decay: 0.007 },
    body: { freq: 66, decay: 0.23, drive: 3.6, sub: 0.62, punch: 0.75 },
    crack: { freq: 950, Q: 0.7, decay: 0.11, level: 0.7 },
    tail: { level: 0.7, decay: 0.85, lp: 1900, delay: 0.026 },
    mech: 0.0,
  },
  enemy_fire: {
    // Same physics, heard from across a room: less transient, more tail, darker.
    level: 0.85,
    transient: { level: 1.9, hp: 1300, peak: 2800, decay: 0.005 },
    body: { freq: 124, decay: 0.1, drive: 2.2, sub: 0.4, punch: 0.45 },
    crack: { freq: 1700, Q: 1.0, decay: 0.06, level: 0.55 },
    tail: { level: 0.75, decay: 0.6, lp: 2000, delay: 0.034 },
    mech: 0.2,
  },
};

/**
 * One gunshot. Builds ~18 nodes; cheap enough to render live, but pre-rendered in
 * practice so that sustained fire never competes with the render thread.
 */
export function gunshot(ac, dest, t, rng, cfg) {
  const p = cfg;
  // Per-instance variation. ±3% pitch is about a half-semitone: audible as "another
  // round from the same gun", not as a different gun.
  const j = 1 + (rng() - 0.5) * 0.06;
  const lvl = p.level * (1 + (rng() - 0.5) * 0.24); // ±~1 dB

  // --- 1. Transient -------------------------------------------------------------
  // Two halves. The noise half supplies the broadband character; the click core is a
  // trio of phase-aligned high sinusoids with sub-millisecond decays, whose summed
  // peak is *deterministic*. Noise alone will not do: the peak of a 0.35 ms window of
  // white noise is essentially a coin toss, so one pre-rendered variation ends up
  // with a 6 dB louder crack than the next and the shot stops sounding like the same
  // weapon. The click core anchors the peak; the noise decorates it.
  {
    const tl = p.transient.level * lvl;
    const src = noiseSource(ac, 'white', rand(rng, 0.94, 1.08));
    const hp = biquad(ac, 'highpass', p.transient.hp * rand(rng, 0.88, 1.14), 0.62);
    const pk = biquad(ac, 'peaking', p.transient.peak * rand(rng, 0.85, 1.18), 1.3, 9);
    const g = perc(ac, t, tl * 0.62, 0.00035, p.transient.decay * rand(rng, 0.85, 1.15));
    chain(src, hp, pk, g, dest);
    startNoise(src, t, 0.03, rng);

    const cf = p.transient.peak * rand(rng, 0.94, 1.07);
    for (const [ratio, lv, dec] of [[1.0, 1.0, 0.0018], [1.63, 0.62, 0.0013], [2.57, 0.36, 0.0009]]) {
      partial(ac, dest, t, cf * ratio, tl * 0.5 * lv, dec, { attack: 0.00006 });
    }
  }

  // --- 2. Body ------------------------------------------------------------------
  {
    const f = p.body.freq * j;
    const o = ac.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(f * 3.8, t);
    o.frequency.exponentialRampToValueAtTime(f, t + 0.028);
    o.frequency.exponentialRampToValueAtTime(f * 0.72, t + 0.028 + p.body.decay);
    // Envelope before the saturator: loud early = harmonically rich, quiet tail =
    // clean. That level-dependent timbre is most of the "weight".
    const g = perc(ac, t, 0.95 * lvl, 0.0012, p.body.decay);
    const ws = waveshaper(ac, p.body.drive);
    chain(o, g, ws, gain(ac, 0.85), dest);
    o.start(t);
    o.stop(t + p.body.decay + 0.1);

    // Inharmonic upper body — stops the fundamental reading as a musical note.
    partial(ac, dest, t, f * 2.03 * rand(rng, 0.98, 1.02), 0.34 * lvl, p.body.decay * 0.55, { type: 'triangle' });

    // Sub: felt more than heard, gives the shot chest impact on real speakers. Kept
    // modest — a 60 Hz sine needs 4 ms just to reach its first crest, so too much of
    // it drags the envelope peak away from the transient and blunts the whole shot.
    partial(ac, dest, t, f * 0.5, p.body.sub * 0.4 * lvl, p.body.decay * 1.7, { glideTo: f * 0.36 });

    // Resonant noise "punch" at the body frequency — the air, not the tone. Q is low
    // on purpose: a high-Q resonator takes Q/(pi*f) seconds to ring up, which is
    // another way to smear the attack.
    noiseBurst(ac, dest, t, rng, {
      freq: f * 1.55 * rand(rng, 0.9, 1.1), Q: 1.6, level: p.body.punch * 0.85 * lvl,
      attack: 0.0012, decay: p.body.decay * 0.85,
    });
  }

  // --- 3. Crack (mid band) ------------------------------------------------------
  {
    const f = p.crack.freq * rand(rng, 0.9, 1.11);
    noiseBurst(ac, dest, t + 0.0008, rng, {
      freq: f, Q: p.crack.Q, level: p.crack.level * lvl,
      attack: 0.0012, decay: p.crack.decay * rand(rng, 0.85, 1.2),
    });
    noiseBurst(ac, dest, t + 0.0008, rng, {
      freq: f * 2.15, Q: p.crack.Q * 1.4, level: p.crack.level * 0.38 * lvl,
      attack: 0.0008, decay: p.crack.decay * 0.55,
    });
  }

  // --- 4. Action / mechanical ---------------------------------------------------
  if (p.mech > 0) {
    metalTick(ac, dest, t + rand(rng, 0.010, 0.016), rng, { level: p.mech * 0.5 * lvl, bright: 1.2 });
    metalTick(ac, dest, t + rand(rng, 0.030, 0.042), rng, { level: p.mech * 0.34 * lvl, bright: 0.85 });
  }

  // --- 5. Tail (the room) -------------------------------------------------------
  {
    const src = noiseSource(ac, 'white', rand(rng, 0.95, 1.05));
    const lp = biquad(ac, 'lowpass', p.tail.lp, 0.8);
    const hp = biquad(ac, 'highpass', 170, 0.7);
    const d = p.tail.decay * rand(rng, 0.9, 1.12);
    const t0 = t + p.tail.delay * rand(rng, 0.85, 1.2);
    // Cutoff falls through the tail: distant reflections have lost their highs.
    sweep(lp.frequency, t0, p.tail.lp * rand(rng, 0.9, 1.1), p.tail.lp * 0.26, d);
    const g = ac.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(p.tail.level * lvl, t0 + 0.014);
    g.gain.exponentialRampToValueAtTime(1e-4, t0 + d);
    g.gain.setValueAtTime(0, t0 + d + 0.002);
    chain(src, hp, lp, g, dest);
    startNoise(src, t0, d + 0.05, rng);

    // Two discrete slapback reflections. Even in a pre-rendered dry buffer these
    // establish "there are walls" before the convolution reverb ever engages.
    for (let i = 0; i < 2; i++) {
      const at = t0 + rand(rng, 0.045, 0.11) + i * rand(rng, 0.05, 0.09);
      noiseBurst(ac, dest, at, rng, {
        filter: 'lowpass', freq: p.tail.lp * 0.55, Q: 0.7,
        level: p.tail.level * 0.28 * lvl * (1 - i * 0.4),
        attack: 0.004, decay: 0.09,
      });
    }
  }
}

/* --------------------------------------------------------------- mechanics --- */

/**
 * A small metallic tick — the atom every weapon mechanism is built from. A noise
 * transient plus three inharmonic ringing partials; the inharmonicity is what makes
 * it steel rather than a woodblock.
 */
export function metalTick(ac, dest, t, rng, { level = 1, bright = 1, decay = 0.035 } = {}) {
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 2600 * bright, Q: 0.7,
    level: level * 0.8, attack: 0.0004, decay: 0.006 * rand(rng, 0.7, 1.4),
  });
  const base = rand(rng, 1750, 2400) * bright;
  modal(ac, dest, t, base, [
    [1.0, level * 0.32, decay],
    [1.83, level * 0.2, decay * 0.7],
    [2.71, level * 0.12, decay * 0.5],
  ], { spread: 0.08, rng });
}

/** Low wooden/polymer thunk — the receiver, the handguard, a magazine seating. */
export function thunk(ac, dest, t, rng, { level = 1, freq = 170, decay = 0.07 } = {}) {
  partial(ac, dest, t, freq * rand(rng, 0.94, 1.06), level * 0.7, decay, { type: 'sine', glideTo: freq * 0.7 });
  noiseBurst(ac, dest, t, rng, {
    freq: freq * 2.4, Q: 1.6, level: level * 0.45, attack: 0.0008, decay: decay * 0.6,
  });
  noiseBurst(ac, dest, t, rng, {
    filter: 'lowpass', freq: 900, Q: 0.8, level: level * 0.3, attack: 0.0006, decay: 0.02,
  });
}

/** Fabric / nylon movement. Filtered pink noise with a swell — pure texture, no pitch. */
export function cloth(ac, dest, t, rng, { level = 1, dur = 0.13, freq = 1900, bright = 1 } = {}) {
  const src = noiseSource(ac, 'pink', rand(rng, 0.85, 1.2));
  const bp = biquad(ac, 'bandpass', freq * bright, 0.85);
  sweep(bp.frequency, t, freq * bright * rand(rng, 0.7, 1.0), freq * bright * rand(rng, 1.1, 1.7), dur);
  const g = shape(ac, t, [
    [0, 0],
    [dur * 0.35, level * 0.6],
    [dur * 0.6, level * 0.35],
    [dur, 1e-5],
  ]);
  chain(src, bp, g, dest);
  startNoise(src, t, dur + 0.03, rng);
  // A little HF air on top so it isn't a pure midrange hiss.
  noiseBurst(ac, dest, t + dur * 0.2, rng, {
    filter: 'highpass', freq: 5200, Q: 0.6, level: level * 0.16, attack: dur * 0.2, decay: dur * 0.7,
  });
}

/** A spring: a short, high, slightly detuned buzz with a fast downward glide. */
function spring(ac, dest, t, rng, { level = 1, freq = 3600, decay = 0.07 } = {}) {
  for (let i = 0; i < 3; i++) {
    const f = freq * rand(rng, 0.85, 1.25) * (1 + i * 0.37);
    partial(ac, dest, t + i * 0.002, f, level * 0.14 / (1 + i), decay * rand(rng, 0.6, 1.2), {
      glideTo: f * 0.72,
    });
  }
}

export function dryfire(ac, dest, t, rng) {
  metalTick(ac, dest, t, rng, { level: 0.85, bright: 1.35, decay: 0.028 });
  metalTick(ac, dest, t + 0.008, rng, { level: 0.25, bright: 0.9, decay: 0.02 });
  spring(ac, dest, t + 0.004, rng, { level: 0.5, freq: 4200, decay: 0.04 });
}

export function magOut(ac, dest, t, rng) {
  // Release catch, magazine sliding free, then clearing the well.
  metalTick(ac, dest, t, rng, { level: 0.8, bright: 1.25, decay: 0.03 });
  spring(ac, dest, t + 0.006, rng, { level: 0.35, freq: 3100, decay: 0.06 });
  cloth(ac, dest, t + 0.03, rng, { level: 0.5, dur: 0.1, freq: 2400 });
  for (let i = 0; i < 3; i++) {
    metalTick(ac, dest, t + rand(rng, 0.05, 0.17), rng, { level: rand(rng, 0.12, 0.26), bright: rand(rng, 0.8, 1.5), decay: 0.02 });
  }
  thunk(ac, dest, t + 0.16, rng, { level: 0.4, freq: 210, decay: 0.06 });
}

export function magIn(ac, dest, t, rng) {
  // Approach rattle, then a solid seat: the low thunk has to arrive last and loudest.
  cloth(ac, dest, t, rng, { level: 0.4, dur: 0.08, freq: 2200 });
  metalTick(ac, dest, t + 0.04, rng, { level: 0.3, bright: 1.1, decay: 0.02 });
  thunk(ac, dest, t + 0.09, rng, { level: 1.0, freq: 155, decay: 0.09 });
  metalTick(ac, dest, t + 0.09, rng, { level: 0.75, bright: 0.95, decay: 0.045 });
  metalTick(ac, dest, t + 0.103, rng, { level: 0.3, bright: 1.6, decay: 0.03 });
}

export function boltRelease(ac, dest, t, rng) {
  // Catch releases, carrier travels, and slams into battery. Three events, ~35 ms.
  metalTick(ac, dest, t, rng, { level: 0.55, bright: 1.5, decay: 0.02 });
  spring(ac, dest, t + 0.002, rng, { level: 0.9, freq: 4800, decay: 0.05 });
  noiseBurst(ac, dest, t + 0.008, rng, {
    filter: 'bandpass', freq: 2400, Q: 1.1, level: 0.35, attack: 0.004, decay: 0.022,
  });
  metalTick(ac, dest, t + 0.034, rng, { level: 1.0, bright: 1.0, decay: 0.06 });
  thunk(ac, dest, t + 0.034, rng, { level: 0.55, freq: 190, decay: 0.05 });
}

export function adsIn(ac, dest, t, rng) {
  cloth(ac, dest, t, rng, { level: 0.75, dur: 0.16, freq: 1500, bright: 0.9 });
  metalTick(ac, dest, t + 0.1, rng, { level: 0.16, bright: 1.4, decay: 0.018 });
}

export function adsOut(ac, dest, t, rng) {
  cloth(ac, dest, t, rng, { level: 0.6, dur: 0.13, freq: 2100, bright: 1.15 });
}

export function weaponSwitch(ac, dest, t, rng) {
  cloth(ac, dest, t, rng, { level: 0.85, dur: 0.22, freq: 1700 });
  metalTick(ac, dest, t + 0.05, rng, { level: 0.35, bright: 0.9, decay: 0.04 });
  thunk(ac, dest, t + 0.13, rng, { level: 0.5, freq: 230, decay: 0.05 });
  metalTick(ac, dest, t + 0.19, rng, { level: 0.45, bright: 1.2, decay: 0.05 });
}

/**
 * A spent case hitting concrete. Signature FPS sound and almost free to make: a few
 * high inharmonic partials, then two or three bounces at shrinking intervals and
 * rising pitch, because a case loses energy but keeps spinning.
 */
export function shellCasing(ac, dest, t, rng, { level = 1, hard = 1 } = {}) {
  const base = rand(rng, 2300, 3400);
  const bounces = 2 + (rng() < 0.55 ? 1 : 0);
  let at = t;
  let amp = level;
  let gapBase = rand(rng, 0.075, 0.13);
  for (let i = 0; i < bounces; i++) {
    const pitch = base * (1 + i * rand(rng, 0.04, 0.12));
    modal(ac, dest, at, pitch, [
      [1.0, amp * 0.5, 0.075 * hard],
      [1.64, amp * 0.34, 0.055 * hard],
      [2.49, amp * 0.22, 0.04 * hard],
      [3.77, amp * 0.11, 0.025 * hard],
    ], { spread: 0.1, rng });
    noiseBurst(ac, dest, at, rng, {
      filter: 'highpass', freq: 4200, Q: 0.7, level: amp * 0.3, attack: 0.0003, decay: 0.005,
    });
    at += gapBase;
    gapBase *= rand(rng, 0.5, 0.72);
    amp *= rand(rng, 0.4, 0.6);
  }
  // Final settle: a brief metallic ring-and-roll.
  noiseBurst(ac, dest, at, rng, {
    filter: 'bandpass', freq: base * 1.2, Q: 6, level: level * 0.12, attack: 0.004, decay: 0.12,
  });
}

export const WEAPON_MECH = {
  dryfire, magOut, magIn, boltRelease, adsIn, adsOut, weaponSwitch, shellCasing,
};
